export interface StableStringifyOptions {
  cycles?: boolean;
  cmp?: (
    a: { key: string; value: any },
    b: { key: string; value: any }
  ) => number;
}

export default function stableStringify(
  data: any,
  opts?:
    | StableStringifyOptions
    | ((
        a: { key: string; value: any },
        b: { key: string; value: any }
      ) => number)
): string | undefined {
  if (!opts) {
    opts = {};
  }
  if (typeof opts === 'function') {
    opts = { cmp: opts };
  }

  const cycles: boolean =
    typeof opts.cycles === 'boolean' ? opts.cycles : false;

  const cmp =
    opts.cmp &&
    (function (
      f: (
        a: { key: string; value: any },
        b: { key: string; value: any }
      ) => number
    ) {
      return function (node: Record<string, any>) {
        return function (a: string, b: string) {
          const aobj = { key: a, value: node[a] };
          const bobj = { key: b, value: node[b] };
          return f(aobj, bobj);
        };
      };
    })(opts.cmp);

  const seen: any[] = [];

  function stringify(node: any): string | undefined {
    if (node && typeof node.toJSON === 'function') {
      node = node.toJSON();
    }

    if (node === undefined) {
      return;
    }
    if (typeof node === 'number') {
      return isFinite(node) ? '' + node : 'null';
    }
    if (typeof node !== 'object') {
      return JSON.stringify(node);
    }

    let out: string;

    if (Array.isArray(node)) {
      out = '[';
      for (let i = 0; i < node.length; i++) {
        if (i) {
          out += ',';
        }
        out += stringify(node[i]) || 'null';
      }
      return out + ']';
    }

    if (node === null) {
      return 'null';
    }

    if (seen.indexOf(node) !== -1) {
      if (cycles) {
        return JSON.stringify('__cycle__');
      }
      throw new TypeError('Converting circular structure to JSON');
    }

    const seenIndex = seen.push(node) - 1;
    const keys = Object.keys(node);
    if (cmp) {
      keys.sort(cmp(node));
    } else {
      keys.sort();
    }
    out = '';
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]!;
      const value = stringify(node[key]);
      if (!value) {
        continue;
      }
      if (out) {
        out += ',';
      }
      out += JSON.stringify(key) + ':' + value;
    }
    seen.splice(seenIndex, 1);
    return '{' + out + '}';
  }

  return stringify(data);
}
